'use client';

import Icon from '@/components/ui/AppIcon';

interface GovernmentEngagementProps {
  currentLanguage: string;
}

const GovernmentEngagement = ({ currentLanguage }: GovernmentEngagementProps) => {
  const content = {
    en: {
      title: 'Government Engagement Portal',
      subtitle: 'Policy alignment and public-private partnership frameworks',
      features: [
        {
          title: 'Policy Alignment Tools',
          icon: 'DocumentTextIcon',
          description: 'Ensure compliance with national and international sustainability policies',
          capabilities: ['BRSR compliance', 'CSRD alignment', 'NetZero roadmap integration', 'Regulatory tracking']
        },
        {
          title: 'PPP Framework Development',
          icon: 'UsersIcon',
          description: 'Structure effective public-private partnerships for infrastructure projects',
          capabilities: ['Risk sharing models', 'Funding structures', 'Performance metrics', 'Stakeholder engagement']
        },
        {
          title: 'Compliance Dashboard',
          icon: 'ChartBarIcon',
          description: 'Real-time monitoring of regulatory requirements and reporting obligations',
          capabilities: ['Automated reporting', 'Audit trails', 'Document management', 'Deadline tracking']
        },
        {
          title: 'Multi-Stakeholder Platform',
          icon: 'BuildingOfficeIcon',
          description: 'Collaborative workspace for government, enterprises, and NGOs',
          capabilities: ['Secure communication', 'Project coordination', 'Resource sharing', 'Progress tracking']
        }
      ],
      engagement: {
        title: 'Engagement Areas',
        areas: [
          { name: 'Central Government', icon: 'BuildingLibraryIcon', projects: '25+' },
          { name: 'State Agencies', icon: 'BuildingOfficeIcon', projects: '40+' },
          { name: 'Municipal Bodies', icon: 'HomeModernIcon', projects: '60+' },
          { name: 'International Orgs', icon: 'GlobeAltIcon', projects: '15+' }
        ]
      }
    },
    hi: {
      title: 'सरकारी सहभागिता पोर्टल',
      subtitle: 'नीति संरेखण और सार्वजनिक-निजी साझेदारी ढांचे',
      features: [
        {
          title: 'नीति संरेखण उपकरण',
          icon: 'DocumentTextIcon',
          description: 'राष्ट्रीय और अंतरराष्ट्रीय स्थिरता नीतियों के साथ अनुपालन सुनिश्चित करें',
          capabilities: ['BRSR अनुपालन', 'CSRD संरेखण', 'NetZero रोडमैप एकीकरण', 'नियामक ट्रैकिंग']
        },
        {
          title: 'PPP ढांचा विकास',
          icon: 'UsersIcon',
          description: 'बुनियादी ढांचा परियोजनाओं के लिए प्रभावी सार्वजनिक-निजी साझेदारी संरचना',
          capabilities: ['जोखिम साझा मॉडल', 'वित्त पोषण संरचनाएं', 'प्रदर्शन मेट्रिक्स', 'हितधारक जुड़ाव']
        },
        {
          title: 'अनुपालन डैशबोर्ड',
          icon: 'ChartBarIcon',
          description: 'नियामक आवश्यकताओं और रिपोर्टिंग दायित्वों की वास्तविक समय निगरानी',
          capabilities: ['स्वचालित रिपोर्टिंग', 'ऑडिट ट्रेल', 'दस्तावेज़ प्रबंधन', 'समय सीमा ट्रैकिंग']
        },
        {
          title: 'बहु-हितधारक मंच',
          icon: 'BuildingOfficeIcon',
          description: 'सरकार, उद्यमों और एनजीओ के लिए सहयोगी कार्यक्षेत्र',
          capabilities: ['सुरक्षित संचार', 'परियोजना समन्वय', 'संसाधन साझा करना', 'प्रगति ट्रैकिंग']
        }
      ],
      engagement: {
        title: 'सहभागिता क्षेत्र',
        areas: [
          { name: 'केंद्र सरकार', icon: 'BuildingLibraryIcon', projects: '25+' },
          { name: 'राज्य एजेंसियां', icon: 'BuildingOfficeIcon', projects: '40+' },
          { name: 'नगरपालिका निकाय', icon: 'HomeModernIcon', projects: '60+' },
          { name: 'अंतरराष्ट्रीय संगठन', icon: 'GlobeAltIcon', projects: '15+' }
        ]
      }
    },
    ar: {
      title: 'بوابة المشاركة الحكومية',
      subtitle: 'أدوات مواءمة السياسات وأطر الشراكة بين القطاعين العام والخاص',
      features: [
        {
          title: 'أدوات مواءمة السياسات',
          icon: 'DocumentTextIcon',
          description: 'ضمان الامتثال لسياسات الاستدامة الوطنية والدولية',
          capabilities: ['امتثال BRSR', 'مواءمة CSRD', 'تكامل خارطة طريق NetZero', 'تتبع التنظيم']
        },
        {
          title: 'تطوير إطار الشراكة',
          icon: 'UsersIcon',
          description: 'هيكلة شراكات فعالة بين القطاعين العام والخاص لمشاريع البنية التحتية',
          capabilities: ['نماذج تقاسم المخاطر', 'هياكل التمويل', 'مقاييس الأداء', 'مشاركة أصحاب المصلحة']
        },
        {
          title: 'لوحة معلومات الامتثال',
          icon: 'ChartBarIcon',
          description: 'المراقبة في الوقت الفعلي للمتطلبات التنظيمية والتزامات التقارير',
          capabilities: ['التقارير الآلية', 'مسارات التدقيق', 'إدارة الوثائق', 'تتبع المواعيد النهائية']
        },
        {
          title: 'منصة متعددة أصحاب المصلحة',
          icon: 'BuildingOfficeIcon',
          description: 'مساحة عمل تعاونية للحكومة والمؤسسات والمنظمات غير الحكومية',
          capabilities: ['اتصال آمن', 'تنسيق المشاريع', 'مشاركة الموارد', 'تتبع التقدم']
        }
      ],
      engagement: {
        title: 'مجالات المشاركة',
        areas: [
          { name: 'الحكومة المركزية', icon: 'BuildingLibraryIcon', projects: '25+' },
          { name: 'وكالات الولاية', icon: 'BuildingOfficeIcon', projects: '40+' },
          { name: 'الهيئات البلدية', icon: 'HomeModernIcon', projects: '60+' },
          { name: 'المنظمات الدولية', icon: 'GlobeAltIcon', projects: '15+' }
        ]
      }
    }
  };

  const currentContent = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <section className="py-16 md:py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {currentContent.features.map((feature, index) => (
            <div 
              key={index}
              className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 hover:shadow-natural transition-all duration-300"
            >
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={feature.icon as any} size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-text-secondary text-sm">
                    {feature.description}
                  </p>
                </div>
              </div>
              <div className="pl-16">
                <ul className="space-y-2">
                  {feature.capabilities.map((capability, cIndex) => (
                    <li key={cIndex} className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                      <span className="text-text-secondary text-sm">{capability}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Engagement Areas */}
        <div className="bg-card border border-border rounded-xl p-8">
          <h3 className="font-heading font-bold text-2xl text-text-primary mb-8 text-center">
            {currentContent.engagement.title}
          </h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {currentContent.engagement.areas.map((area, index) => (
              <div 
                key={index}
                className="text-center p-6 bg-background rounded-lg border border-border hover:border-primary/50 transition-colors"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={area.icon as any} size={32} className="text-primary" />
                </div>
                <h4 className="font-semibold text-text-primary mb-2">{area.name}</h4>
                <div className="text-2xl font-bold text-primary">{area.projects}</div>
                <div className="text-xs text-text-secondary">Active Projects</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GovernmentEngagement;